import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import MatchTimerPill from './MatchTimerPill';
import { IconMap, IconGamepad, IconServer, IconBarChart } from './Icons';
import PublicHeader from './PublicHeader';
import Footer from './Footer';
import Veto from './Veto';
import { formatShortDateTime } from '../utils/timeUtils';
import './PublicMatches.css';
import usePageTitle from '../utils/usePageTitle';
import { matchTabTitle } from '../utils/matchTitle';
import MapDuration from './match/MapDuration';
import PublicMatchList from './public/PublicMatchList';
import MatchFeed from './feed/MatchFeed';

// Public API client — never sends auth token so server always applies public sanitization
const publicApi = axios.create();
delete publicApi.defaults.headers.common['Authorization'];

const ROUND_END_REASON_LABELS = {
  t_elimination: 'CT Eliminated',
  ct_elimination: 'Terrorist Eliminated',
  bomb_defused: 'C4 Defused',
  bomb_exploded: 'C4 Exploded',
  time: 'Time Ran Out',
};

function formatRoundEndReason(reason) {
  return ROUND_END_REASON_LABELS[reason] || reason;
}

// "/" → the public match list (public/PublicMatchList.js); "/match/:id" → the match page below.
function PublicMatches() {
  const { id } = useParams();
  return id ? <PublicMatchPage /> : <PublicMatchList />;
}

function PublicMatchPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [match, setMatch] = useState(null);
  const [matchTimer, setMatchTimer] = useState(null); // live round timer (match_timer_update)
  // Tab title with the live score ("[11-1] A vs B", "Tech - A | …" during a tech pause). Same data the
  // page shows: for a stream-delayed match the public API and /ws/public are already delayed, so the
  // tab never runs ahead of the stream.
  usePageTitle(id ? matchTabTitle(match, matchTimer) : t('seo.matchesTitle', 'Live CS2 matches'));
  const [roundHistory, setRoundHistory] = useState([]);
  const [selectedMapIndex, setSelectedMapIndex] = useState(null);
  const [selectedOtPeriod, setSelectedOtPeriod] = useState(null); // null = regulation, 1 = OT1, 2 = OT2, ...
  // Desktop-only: open the map veto as an overlay on top of the match page instead
  // of navigating to /veto/:id. On mobile we keep the full-page navigation.
  const [vetoOverlayRoom, setVetoOverlayRoom] = useState(null);
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 900);
  const [loading, setLoading] = useState(true);
  const [feedTick, setFeedTick] = useState(0); // bumped on kill_event → the kill feed fetches new events
  // Per-relay "copied" feedback for the public CSTV connect strings, keyed by relay number (1|2).
  const [cstvCopied, setCstvCopied] = useState(null);
  // Per-relay reveal state for the masked CSTV IP, keyed by relay number. IP is masked
  // by default on the public page; clicking the eye reveals it. Copy always copies the
  // full unmasked connect command regardless of reveal state.
  const [cstvRevealed, setCstvRevealed] = useState({});
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const mountedRef = useRef(true);

  // Track desktop vs mobile for the veto overlay behavior.
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Close the veto overlay on Escape; lock body scroll while it's open.
  useEffect(() => {
    if (!vetoOverlayRoom) return;
    const onKey = (e) => { if (e.key === 'Escape') setVetoOverlayRoom(null); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [vetoOverlayRoom]);

  useEffect(() => {
    mountedRef.current = true;
    // match page: always fresh poll (the list view is public/PublicMatchList.js)
    const pollInterval = setInterval(() => {
      if (!mountedRef.current) return;
      fetchMatch();
      fetchRoundHistory();
    }, 5000);
    return () => clearInterval(pollInterval);
  }, [id]);

  useEffect(() => {
    mountedRef.current = true;
    setMatchTimer(null);
    fetchMatch();
    fetchRoundHistory();

    function connect() {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = (host === 'localhost' || host === '127.0.0.1') ? ':5000' : (window.location.port ? `:${window.location.port}` : '');
      const ws = new WebSocket(`${protocol}//${host}${port}/ws/public`);
      wsRef.current = ws;
      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (id && msg.data && msg.data.match_id === parseInt(id)) {
            if (msg.type === 'score_update') {
              setMatch(p => p ? { ...p, score_team1: msg.data.score_team1, score_team2: msg.data.score_team2 } : null);
              fetchRoundHistory();
            }
            else if (msg.type === 'match_stage_change') setMatch(p => p ? { ...p, match_stage: msg.data.stage } : null);
            else if (msg.type === 'match_timer_update') setMatchTimer(msg.data);
            else if (msg.type === 'kill_event') setFeedTick(n => n + 1);
            else if (msg.type === 'match_update') {
              setMatch(p => p ? { ...p, ...msg.data } : null); // show_killfeed on / off mounts / unmounts the feed
              // CSTV connect info changed — re-fetch so the (public-gated) IP/password appear now.
              if (msg.data.cstv_updated) fetchMatch();
            }
            else if (msg.type === 'demo_status_update') fetchMatch();
            else if (['player_stats_update', 'match_started', 'match_finished'].includes(msg.type)) {
              fetchMatch();
              fetchRoundHistory();
            }
          }
          if (msg.type === 'tech_pause' && msg.data) window.dispatchEvent(new CustomEvent('techPause', { detail: msg.data }));
        } catch (_) { }
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (mountedRef.current) reconnectRef.current = setTimeout(connect, 3000);
      };
    }
    connect();

    return () => {
      mountedRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [id]);

  const fetchMatch = async () => {
    if (!id || id === 'undefined') return;
    try {
      const r = await publicApi.get(`/api/matches/${id}`);
      if (mountedRef.current) { setMatch(r.data); setLoading(false); }
    } catch (_) { if (mountedRef.current) setLoading(false); }
  };

  const fetchRoundHistory = async () => {
    if (!id || id === 'undefined') return;
    try {
      const response = await publicApi.get(`/api/matches/${id}/rounds`);
      if (mountedRef.current) { setRoundHistory(response.data.rounds || []); }
    } catch (error) { }
  };

  // Kill feed (feed/MatchFeed.js) loads itself, polls while live and refetches on feedTick; its height
  // follows the stats card via CSS (see .pub-match-content-grid in PublicMatches.css).

  // Smart-default the selected map tab once match data is available
  useEffect(() => {
    if (match) {
      const currentIdx = typeof match.current_map_index === 'number' ? match.current_map_index : parseInt(match.current_map_index, 10) || 0;
      if (match.status === 'finished' || match.status === 'cancelled') {
        setSelectedMapIndex(0);
      } else {
        setSelectedMapIndex(currentIdx);
      }
    }
  }, [match?.id, match?.status, match?.current_map_index]);

  const statusConfig = (status, stage) => {
    if (status === 'live') return { label: t('matchStatus.live', 'Live'), cls: 'pub-badge-live', dot: true };
    if (status === 'finished') return { label: t('matchStatus.finished', 'Finished'), cls: 'pub-badge-done' };
    if (status === 'cancelled') return { label: t('matchStatus.cancelled', 'Cancelled'), cls: 'pub-badge-cancel' };
    const s = stage || status || 'pending';
    return { label: t(`matchStatus.${s}`, s.charAt(0).toUpperCase() + s.slice(1)), cls: 'pub-badge-pending' };
  };

  if (loading) return (
    <div className="pub-loading">
      <div className="pub-spinner" />
    </div>
  );

  if (id && !match) {
    return (
      <div className="pub-root">
        <PublicHeader />
        <div className="pub-container pub-list-container">
          <div className="pub-empty-state">
            <div className="pub-empty-icon"><IconGamepad size={28} color="#4a4a5a" /></div>
            <p>Match not found.</p>
            <Link to="/matches" className="pub-back">Back to Matches</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  /* ── DETAIL VIEW ─────────────────────────────────────────── */
  if (id && match) {
    const detailStatus = statusConfig(match.status, match.match_stage);
    const currentMapIndex = typeof match.current_map_index === 'number'
      ? match.current_map_index
      : parseInt(match.current_map_index, 10) || 0;
    const activeMapIndex = selectedMapIndex ?? currentMapIndex;

    const getTeamSides = () => {
      let mapSides = null;
      try { mapSides = typeof match.map_sides === 'string' ? JSON.parse(match.map_sides) : match.map_sides; } catch (e) { }

      const startingValue = (Array.isArray(mapSides) && mapSides[activeMapIndex])
        ? mapSides[activeMapIndex]
        : (match.starting_side || 'team1_ct');

      const startingTeam1IsCt = (startingValue === 'team1_ct');

      const r = (match.score_team1 || 0) + (match.score_team2 || 0);
      const mpMaxRounds = match?.mp_maxrounds || 24;
      const mpOtMaxRounds = match?.mp_overtime_maxrounds || 6;
      const roundsPerHalf = mpMaxRounds / 2;
      const roundsPerOTHalf = mpOtMaxRounds / 2;

      let flipped = r >= roundsPerHalf;
      if (r >= mpMaxRounds) {
        const otRound = r - mpMaxRounds;
        const otHalf = Math.floor(otRound / roundsPerOTHalf);
        if (otHalf % 2 === 1) flipped = !flipped;
      }

      const team1IsCt = flipped ? !startingTeam1IsCt : startingTeam1IsCt;
      const team1Side = team1IsCt ? 'CT' : 'T';
      const team2Side = team1IsCt ? 'T' : 'CT';

      const startingSide1 = startingTeam1IsCt ? 'CT' : 'T';
      const startingSide2 = startingTeam1IsCt ? 'T' : 'CT';

      return { team1Side, team2Side, startingSide1, startingSide2 };
    };

    const getMergedStats = (basePlayers, mapIndex, teamKey) => {
      if (mapIndex == null) return basePlayers || [];
      if (!basePlayers) return [];

      const mapStatsObj = (match.stats_by_map && match.stats_by_map[mapIndex]) || {};
      const specificPlayers = mapStatsObj[teamKey] || [];

      const statsLookup = new Map();
      specificPlayers.forEach(p => statsLookup.set(p.steam_id, p));

      const merged = basePlayers.map(p => {
        const specific = statsLookup.get(p.steam_id);
        if (specific) {
          return { ...p, ...specific };
        }
        return {
          ...p,
          kills: 0, deaths: 0, assists: 0, mvps: 0, score: 0, damage: 0
        };
      }).sort((a, b) => (b.score || 0) - (a.score || 0));

      // On a FINISHED/cancelled match viewing a SPECIFIC map, hide roster players with
      // zero activity on that map (no kills/deaths/assists/damage/score) — they never
      // actually played it. Mirrors the admin scoreboard's "ghost player" filter, for
      // players whose server-side hidden_maps was never set. Scoped to finished +
      // concrete map index so it never hides someone mid-match or in the "all maps" view.
      const matchDone = match.status === 'finished' || match.status === 'cancelled';
      const specificMap = typeof activeMapIndex === 'number';
      const didNotPlayMap = (p) => (
        (p.kills || 0) === 0 && (p.deaths || 0) === 0 && (p.assists || 0) === 0 &&
        (p.damage || 0) === 0 && (p.score || 0) === 0
      );

      const visible = [];
      const hidden = [];
      merged.forEach(p => {
        const hiddenMaps = p.hidden_maps || [];
        if (hiddenMaps.includes(activeMapIndex) || hiddenMaps.includes('all')) {
          hidden.push(p);
        } else if (matchDone && specificMap && didNotPlayMap(p)) {
          hidden.push(p);
        } else {
          visible.push(p);
        }
      });

      return { visible, hidden };
    };

    const { team1Side, team2Side, startingSide1, startingSide2 } = getTeamSides();
    const currentStats = {
      team1: getMergedStats(match.team1_players, activeMapIndex, 'team1_players'),
      team2: getMergedStats(match.team2_players, activeMapIndex, 'team2_players')
    };

    const filteredRounds = activeMapIndex !== 'all'
      ? roundHistory.filter(r => r.map_index === activeMapIndex)
      : roundHistory;

    let displayScore1, displayScore2;
    let scoreLabel;

    let mapStats = [];
    try { mapStats = match.map_stats ? (typeof match.map_stats === 'string' ? JSON.parse(match.map_stats) : match.map_stats) : []; } catch (e) { }

    if (activeMapIndex === 'all' && match.best_of > 1) {
      displayScore1 = match.map_wins_team1 || 0;
      displayScore2 = match.map_wins_team2 || 0;
      scoreLabel = `Best of ${match.best_of} (Match Score)`;
    } else if (activeMapIndex === 'all' && Number(match.best_of) === 1) {
      displayScore1 = match.score_team1 || 0;
      displayScore2 = match.score_team2 || 0;
      scoreLabel = match.map ? match.map.replace(/^(de_|cs_)/, '') : 'Live Score';
    } else {
      if (mapStats[activeMapIndex]) {
        displayScore1 = mapStats[activeMapIndex].score_team1;
        displayScore2 = mapStats[activeMapIndex].score_team2;
      } else {
        if (activeMapIndex === currentMapIndex) {
          displayScore1 = match.score_team1 || 0;
          displayScore2 = match.score_team2 || 0;
        } else if (filteredRounds.length === 0) {
          // No round data available — check forfeit or map_stats entry
          const forfeitEntry = mapStats.find(s => s.map_index === activeMapIndex && s.forfeit);
          if (forfeitEntry) {
            displayScore1 = forfeitEntry.score_team1 ?? 0;
            displayScore2 = forfeitEntry.score_team2 ?? 0;
          } else if (mapStats[activeMapIndex]) {
            displayScore1 = mapStats[activeMapIndex].score_team1 ?? 0;
            displayScore2 = mapStats[activeMapIndex].score_team2 ?? 0;
          } else {
            displayScore1 = 0;
            displayScore2 = 0;
          }
        } else {
          const MR = 12;
          displayScore1 = 0;
          displayScore2 = 0;

          filteredRounds.forEach((r) => {
            const roundNum = r.round_number || 0;
            const isFirstHalf = roundNum <= MR;

            const currentSide1 = isFirstHalf ? startingSide1 : (startingSide1 === 'CT' ? 'TERRORIST' : 'CT');
            const currentSide2 = isFirstHalf ? startingSide2 : (startingSide2 === 'CT' ? 'TERRORIST' : 'CT');

            const winnerSide = r.winner_side === 'T' ? 'TERRORIST' : r.winner_side;

            if (winnerSide === currentSide1) displayScore1++;
            else if (winnerSide === currentSide2) displayScore2++;
          });
        }
      }
      scoreLabel = `Map ${activeMapIndex + 1} Score`;
    }

    // Series score (map wins) — used per-team under each team name.
    // Live-compute from map_stats for up-to-date values; fall back to persisted map_wins_team*.
    // Only meaningful when best_of > 1 (BO3/BO5).
    const _bo = Number(match.best_of) || 1;
    let _series1 = match.map_wins_team1 || 0;
    let _series2 = match.map_wins_team2 || 0;
    if (Array.isArray(mapStats) && mapStats.length > 0) {
      let s1 = 0, s2 = 0;
      for (const m of mapStats) {
        if (!m) continue;
        const s1m = Number(m.score_team1) || 0;
        const s2m = Number(m.score_team2) || 0;
        if (s1m === 0 && s2m === 0 && !m.forfeit) continue;
        if (s1m > s2m) s1++;
        else if (s2m > s1m) s2++;
      }
      _series1 = s1;
      _series2 = s2;
    }
    const _showSeries = _bo > 1;

    let headerMapName = match.map ? match.map.replace(/^(de_|cs_)/, '') : 'Unknown Map';
    // Raw map name (e.g. "de_dust2") for the SELECTED map, so the header background
    // image tracks the map tab the viewer clicked — not just the current live map.
    let headerMapRaw = match.map || null;
    if (activeMapIndex !== 'all') {
      try {
        const maps = match.map_list ? (typeof match.map_list === 'string' ? JSON.parse(match.map_list) : match.map_list) : [];
        if (maps[activeMapIndex]) {
          headerMapRaw = maps[activeMapIndex];
          headerMapName = maps[activeMapIndex].replace(/^(de_|cs_)/, '');
        }
      } catch (e) { }
    }
    // Background image for the selected map. Prefer the per-map GitHub image (same
    // source the panel uses); fall back to the match's stored current-map image.
    const headerMapImageUrl = (activeMapIndex !== 'all' && headerMapRaw)
      ? `https://raw.githubusercontent.com/rpkaul/cs-map-images/refs/heads/main/${headerMapRaw}.png`
      : (match.map_image_url || null);

    const renderFaceitBadge = (faceit) => {
      if (!faceit) return null;
      const hasLevel = faceit.skill_level !== null && faceit.skill_level !== undefined;
      const hasElo = faceit.faceit_elo !== null && faceit.faceit_elo !== undefined;
      if (!hasLevel && !hasElo) return null;

      const level = hasLevel ? faceit.skill_level : '?';
      const levelNumber = Number(level);
      const iconLevel = Number.isInteger(levelNumber) && levelNumber >= 1 && levelNumber <= 10 ? levelNumber : null;
      const elo = hasElo ? Math.round(Number(faceit.faceit_elo)) : null;
      const title = `FACEIT${faceit.nickname ? ` ${faceit.nickname}` : ''}: Level ${level}${elo !== null ? `, ${elo} ELO` : ''}`;

      return (
        <span
          title={title}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            height: 20,
            padding: '0 5px 0 3px',
            borderRadius: 'var(----radius-sm)',
            background: 'rgba(255,255,255,0.035)',
            border: '1px solid rgba(255,255,255,0.08)',
            color: 'var(----accent-amber)',
            fontSize: '0.62rem',
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            lineHeight: 1,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          {iconLevel ? (
            <img
              src={`/faceit-levels/skill_level_${iconLevel}.png`}
              alt={`FACEIT Level ${iconLevel}`}
              style={{ width: 16, height: 16, display: 'block', flexShrink: 0 }}
            />
          ) : (
            <span>F{level}</span>
          )}
          {elo !== null && <span style={{ color: 'var(----text-secondary)' }}>{elo}</span>}
        </span>
      );
    };

    const PlayerRow = ({ p }) => {
      const steamProfileUrl = p.steam_id ? `https://steamcommunity.com/profiles/${p.steam_id}` : null;
      const nameContent = steamProfileUrl ? (
        <a href={steamProfileUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-primary)', textDecoration: 'none', transition: 'color 0.12s' }} onMouseEnter={e => e.currentTarget.style.color = 'var(----accent-cyan)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--text-primary)'}>
          {p.name || p.steam_id || 'Unknown'}
        </a>
      ) : (p.name || p.steam_id || 'Unknown');

      const k = p.kills ?? 0;
      const d = p.deaths ?? 0;
      const a = p.assists ?? 0;
      const hs = p.headshots ?? 0;
      const fk = p.entry_kills ?? 0;
      const tk = p.trade_kills ?? 0;
      const mk2 = p.multikill_2k ?? 0;
      const mk3 = p.multikill_3k ?? 0;
      const mk4 = p.multikill_4k ?? 0;
      const mk5 = p.multikill_5k ?? 0;
      const kastRounds = p.kast_rounds ?? 0;
      const dmg = p.damage ?? 0;

      // Use server-calculated ADR and KAST if available, otherwise calculate from client data
      const adr = p.adr != null ? Number(p.adr).toFixed(1) : (filteredRounds.length > 0 ? (dmg / filteredRounds.length).toFixed(1) : '0.0');
      const kastPct = p.kast_pct != null ? Number(p.kast_pct) : (filteredRounds.length > 0 ? Math.round((kastRounds / filteredRounds.length) * 100) : 0);

      const hsPercent = k > 0 ? Math.round((hs / k) * 100) : 0;
      const kd = (k / Math.max(d, 1)).toFixed(2);
      const rating = Number((p.hltv_rating ?? p.rating) ?? 0);
      const ratingDisplay = filteredRounds.length > 0 ? Math.max(0, rating).toFixed(2) : '0.00';
      const ratingColor = rating >= 1.4 ? '#22c55e' : rating >= 1.2 ? '#facc15' : rating >= 1.0 ? 'var(----text-secondary)' : '#ef4444';
      const cellStyle = { padding: '5px 6px', textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: '0.7rem', whiteSpace: 'nowrap' };

      return (
        <tr className="pub-player-row">
          <td className="pub-td-name">
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
              <span style={{ color: 'var(----text-primary)', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nameContent}
              </span>
              {renderFaceitBadge(p.faceit)}
            </span>
          </td>
          <td style={{ ...cellStyle, color: 'var(----accent-green)' }}>{k}</td>
          <td style={{ ...cellStyle, color: 'var(----accent-red)' }}>{d}</td>
          <td style={{ ...cellStyle, color: 'var(--accent-blue)' }}>{a}</td>
          <td style={{ ...cellStyle, color: kd >= 1 ? 'var(----accent-green)' : 'var(----accent-red)' }}>{kd}</td>
          <td style={{ ...cellStyle, color: 'var(----text-secondary)' }}>{dmg}</td>
          <td style={{ ...cellStyle, color: Number(adr) >= 80 ? 'var(----accent-green)' : 'var(----text-secondary)' }}>{adr}</td>
          <td style={{ ...cellStyle, color: kastPct >= 70 ? 'var(----accent-green)' : 'var(----text-secondary)' }}>{kastPct}%</td>
          <td style={{ ...cellStyle, color: ratingColor, fontWeight: 700 }}>{ratingDisplay}</td>
          {p.flux3_rating != null && (() => {
            const f3 = Number(p.flux3_rating);
            const sw = Number(p.swing_pct ?? 0);
            const f3Color = f3 >= 1.4 ? '#22c55e' : f3 >= 1.2 ? '#facc15' : f3 >= 1.0 ? 'var(----text-secondary)' : '#ef4444';
            return (<>
              <td style={{ ...cellStyle, color: f3Color, fontWeight: 700 }}>{filteredRounds.length > 0 ? Math.max(0, f3).toFixed(2) : '0.00'}</td>
              <td style={{ ...cellStyle, color: sw > 0 ? 'var(----accent-green)' : sw < 0 ? 'var(----accent-red)' : 'var(----text-muted)' }}>{sw > 0 ? '+' : ''}{sw.toFixed(2)}%</td>
            </>);
          })()}
          <td style={{ ...cellStyle, color: hsPercent >= 50 ? '#facc15' : 'var(----text-secondary)' }}>{hsPercent}%</td>
          <td style={{ ...cellStyle, color: 'var(----text-secondary)' }}>{hs}</td>
          <td style={{ ...cellStyle, color: fk > 0 ? '#fb923c' : 'var(----text-muted)' }}>{fk}</td>
          <td style={{ ...cellStyle, color: tk > 0 ? '#a78bfa' : 'var(----text-muted)' }}>{tk}</td>
          <td style={{ ...cellStyle, color: mk2 > 0 ? 'var(----text-secondary)' : 'var(----text-muted)' }}>{mk2}</td>
          <td style={{ ...cellStyle, color: mk3 > 0 ? '#22d3ee' : 'var(----text-muted)' }}>{mk3}</td>
          <td style={{ ...cellStyle, color: mk4 > 0 ? '#facc15' : 'var(----text-muted)' }}>{mk4}</td>
          <td style={{ ...cellStyle, color: mk5 > 0 ? '#ef4444' : 'var(----text-muted)' }}>{mk5}</td>
        </tr>
      );
    };

    return (
      <div className="pub-root">
        <PublicHeader />
        <div className="pub-container pub-detail-container" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '0', paddingBottom: '2rem' }}>
          {/* ── Back + Status ───────────────────────── */}
          <div className="pub-detail-nav">
            <Link to="/matches" className="pub-back">Back to Matches</Link>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
            <span className="pub-status-pill" style={{
              background: match.status === 'live' ? 'rgba(34,197,94,0.08)' : match.status === 'finished' ? 'rgba(139,139,158,0.06)' : 'rgba(245,158,11,0.08)',
              color: match.status === 'live' ? 'var(--accent-green)' : match.status === 'finished' ? 'var(--text-secondary)' : 'var(--accent-amber)',
              border: `1px solid ${match.status === 'live' ? 'rgba(34,197,94,0.2)' : match.status === 'finished' ? 'var(--border-subtle)' : 'rgba(245,158,11,0.2)'}`,
            }}>
              {match.status === 'live' && (
                <span className="pub-live-dot" />
              )}
              {detailStatus.label}
            </span>
            {/* time of the map shown (live → finish); stream-delayed like the rest of the page */}
            <MapDuration matchId={match.id} status={match.status} anonymous mapIndex={activeMapIndex} />
          </div>
            {match.season_id && (
              <Link
                to={`/season/${match.season_id}`}
                title={t('leaderboard.title', 'Leaderboard')}
                style={{
                  marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 12px', borderRadius: 'var(--radius-sm, 6px)', textDecoration: 'none',
                  fontSize: '0.78rem', fontWeight: 600,
                  background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', color: 'var(--accent-blue)',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.16)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(59,130,246,0.08)'}
              >
                <IconBarChart size={13} /> {t('leaderboard.title', 'Leaderboard')}
              </Link>
            )}
          </div>

          {/* ── Hero Scoreboard ─────────────────────── */}
          <div className="pub-scoreboard">
            {headerMapImageUrl && (
              <div className="pub-scoreboard-bg" style={{ backgroundImage: `url(${headerMapImageUrl})` }} />
            )}
            <div className="pub-scoreboard-inner">
              <div className="pub-scoreboard-matchup" style={{ gap: _showSeries ? '0.5rem' : undefined }}>
                <div className="pub-scoreboard-team" style={{ textAlign: 'right', color: team1Side === 'CT' ? '#22d3ee' : '#fb923c', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.6rem' }}>
                  <div>
                    {match.team1_exists ? (
                      <Link to={`/teams/${match.team1_id}`} style={{ color: 'inherit', textDecoration: 'none', transition: 'opacity 0.12s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.7'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>{match.team1_name}</Link>
                    ) : match.team1_name}
                  </div>
                  {match.team1_avatar && match.team1_exists && (
                    <img src={`/api/teams/${match.team1_id}/avatar?v=${match.team1_id}`} alt="" style={{ width: 36, height: 36, borderRadius: '8px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                  )}
                </div>

                {/* Team 1 Series Score (BO3/BO5 only) */}
                {_showSeries && (
                  <div title={`${match.team1_name} map wins`} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, minWidth: 36,
                  }}>
                    <span style={{
                      fontSize: '1rem', fontWeight: 800, fontFamily: 'var(--font-mono)',
                      color: match.status === 'finished' ? (_series1 > _series2 ? '#22c55e' : _series1 < _series2 ? '#ef4444' : 'var(----text-primary)') : 'var(----text-primary)', lineHeight: 1,
                      textShadow: _series1 > 0 ? `0 0 20px ${team1Side === 'CT' ? '#22d3ee' : '#fb923c'}40` : 'none',
                    }}>{_series1}</span>
                  </div>
                )}

                <div className="pub-scoreboard-scores" style={{ flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className="pub-scoreboard-num">{displayScore1}</span>
                    <span className="pub-scoreboard-sep">:</span>
                    <span className="pub-scoreboard-num">{displayScore2}</span>
                  </div>
                  {_showSeries && (
                    <div
                      title={`Best of ${_bo} series score`}
                      style={{
                        display: 'inline-flex', alignItems: 'center',
                        padding: '2px 10px', borderRadius: 'var(----radius-sm)',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.06)',
                        fontFamily: 'var(--font-mono)', fontSize: '0.7rem', fontWeight: 700,
                        color: 'var(----text-secondary)',
                        letterSpacing: '0.04em', lineHeight: 1.4,
                      }}
                    >
                      BO{_bo}
                    </div>
                  )}
                  <MatchTimerPill match={match} matchTimer={matchTimer} style={{ marginTop: 0 }} />
                </div>

                {/* Team 2 Series Score (BO3/BO5 only) */}
                {_showSeries && (
                  <div title={`${match.team2_name} map wins`} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, minWidth: 36,
                  }}>
                    <span style={{
                      fontSize: '1rem', fontWeight: 800, fontFamily: 'var(--font-mono)',
                      color: match.status === 'finished' ? (_series2 > _series1 ? '#22c55e' : _series2 < _series1 ? '#ef4444' : 'var(----text-primary)') : 'var(----text-primary)', lineHeight: 1,
                      textShadow: _series2 > 0 ? `0 0 20px ${team2Side === 'CT' ? '#22d3ee' : '#fb923c'}40` : 'none',
                    }}>{_series2}</span>
                  </div>
                )}

                <div className="pub-scoreboard-team" style={{ textAlign: 'left', color: team2Side === 'CT' ? '#22d3ee' : '#fb923c', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                  {match.team2_avatar && match.team2_exists && (
                    <img src={`/api/teams/${match.team2_id}/avatar?v=${match.team2_id}`} alt="" style={{ width: 36, height: 36, borderRadius: '8px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                  )}
                  <div>
                    {match.team2_exists ? (
                      <Link to={`/teams/${match.team2_id}`} style={{ color: 'inherit', textDecoration: 'none', transition: 'opacity 0.12s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.7'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>{match.team2_name}</Link>
                    ) : match.team2_name}
                  </div>
                </div>
              </div>

              {/* Half-score breakdown - e.g. (9:3; 4:3). Mirrors the admin match page. */}
              {filteredRounds.length > 0 && activeMapIndex !== 'all' && (() => {
                const mpMaxRounds = match?.mp_maxrounds || 24;
                const mpOtMaxRounds = match?.mp_overtime_maxrounds || 6;
                const roundsPerHalf = mpMaxRounds / 2;
                const roundsPerOTHalf = mpOtMaxRounds / 2;

                const h1 = filteredRounds.filter(r => r.round_number >= 1 && r.round_number <= roundsPerHalf);
                const h2 = filteredRounds.filter(r => r.round_number >= roundsPerHalf + 1 && r.round_number <= mpMaxRounds);
                const ot = filteredRounds.filter(r => r.round_number > mpMaxRounds);

                const ss1 = startingSide1 === 'T' ? 'TERRORIST' : startingSide1;

                const countWins = (rounds, isFirstHalf) => {
                  let t1 = 0, t2 = 0;
                  rounds.forEach(r => {
                    const ws = r.winner_side === 'T' ? 'TERRORIST' : r.winner_side;
                    const s1 = isFirstHalf ? ss1 : (ss1 === 'CT' ? 'TERRORIST' : 'CT');
                    if (ws === s1) t1++; else t2++;
                  });
                  return [t1, t2];
                };

                const [h1t1, h1t2] = countWins(h1, true);
                const [h2t1, h2t2] = countWins(h2, false);

                const parts = [];
                if (h1.length > 0) parts.push(`${h1t1}:${h1t2}`);
                if (h2.length > 0) parts.push(`${h2t1}:${h2t2}`);

                for (let i = 0; i < ot.length; i += mpOtMaxRounds) {
                  const otSlice = ot.slice(i, i + mpOtMaxRounds);
                  let ot1 = 0, ot2 = 0;
                  otSlice.forEach((r, idx) => {
                    const otHalfIdx = Math.floor(idx / roundsPerOTHalf);
                    const isOtFirstHalf = otHalfIdx % 2 === 0;
                    const s1 = isOtFirstHalf ? (ss1 === 'CT' ? 'TERRORIST' : 'CT') : ss1;
                    const ws = r.winner_side === 'T' ? 'TERRORIST' : r.winner_side;
                    if (ws === s1) ot1++; else ot2++;
                  });
                  parts.push(`${ot1}:${ot2}`);
                }

                if (parts.length === 0) return null;
                return (
                  <div style={{ textAlign: 'center', position: 'relative', zIndex: 1, marginTop: -2, marginBottom: 8 }}>
                    <span style={{
                      fontSize: '0.78rem', color: 'var(--text-secondary)', fontWeight: 600,
                      letterSpacing: '0.03em', background: 'rgba(0,0,0,0.25)', padding: '2px 10px',
                      borderRadius: 9999, display: 'inline-block', fontFamily: 'var(--font-mono)',
                    }}>
                      ({parts.join('; ')})
                    </span>
                  </div>
                );
              })()}

              {/* ── Match Meta ─────────────────────────────── */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.25rem', flexWrap: 'wrap', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                {(match.created_at) && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    {formatShortDateTime(match.created_at)}
                  </span>
                )}
                {(match.server_name || match.server_city || match.server_country_name) && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                    <IconServer size={11} />
                    {match.server_country_code && (
                      <img src={`https://flagcdn.com/16x12/${match.server_country_code.toLowerCase()}.png`} alt={match.server_country_code} style={{ width: 14, height: 10, borderRadius: 1 }} onError={e => { e.target.style.display = 'none'; }} />
                    )}
                    {[match.server_city, match.server_country_name].filter(Boolean).join(', ') || match.server_name}
                  </span>
                )}
              </div>

              <div className="pub-scoreboard-chips">
                <span className="pub-chip">
                  <IconMap size={12} /> {headerMapName}
                </span>
                <span className="pub-chip">BO{match.best_of || 1}</span>
                <span className="pub-chip" style={{ color: 'var(--text-secondary)' }}>{scoreLabel}</span>
                {match.veto_session_id && (
                  <Link
                    to={`/veto/${match.veto_session_id}`}
                    className="pub-chip"
                    style={{ textDecoration: 'none', cursor: 'pointer', color: 'var(--accent-blue)', border: '1px solid rgba(59,130,246,0.2)', background: 'rgba(59,130,246,0.06)', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.12)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.06)'; }}
                    onClick={(e) => {
                      // Desktop: open as an overlay on top of the match page.
                      // Mobile: fall through to normal full-page navigation.
                      if (isDesktop) {
                        e.preventDefault();
                        setVetoOverlayRoom(match.veto_session_id);
                      }
                    }}
                  >
                    <IconGamepad size={12} /> Map Veto
                  </Link>
                )}
                {match.bracket_id && (
                  <Link to={`/bracket/${match.bracket_id}`} className="pub-chip" style={{ textDecoration: 'none', cursor: 'pointer', color: '#c084fc', border: '1px solid rgba(168,85,247,0.2)', background: 'rgba(168,85,247,0.06)', transition: 'all 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = 'rgba(168,85,247,0.12)'; }} onMouseLeave={e => { e.currentTarget.style.background = 'rgba(168,85,247,0.06)'; }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/></svg>
                    Bracket
                  </Link>
                )}
              </div>

              {/* Demo Download Buttons */}
              {(match.demo_links || []).length > 0 && (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'center', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                  {(match.demo_links || []).map(link => {
                    const mapIdx = link.map_index;
                    let mapLabel = 'Demo';
                    try {
                      const maps = match.map_list ? (typeof match.map_list === 'string' ? JSON.parse(match.map_list) : match.map_list) : [];
                      if (maps.length > 1) {
                        mapLabel = maps[mapIdx] ? maps[mapIdx].replace(/^(de_|cs_)/, '') : `Map ${mapIdx + 1}`;
                      }
                    } catch (e) { }
                    return (
                      <a
                        key={mapIdx}
                        href={link.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="pub-demo-btn"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        {(match.demo_links || []).length > 1 ? `Download - ${mapLabel}` : 'Download Demo'}
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ── Watch on CSTV (below the hero banner) ─── */}
          {/* Only relays the admin toggled public reach here - the API strips the IP +
              password of any non-public relay, so presence of cstvN_ip == "is public". */}
          {(() => {
            const relays = [
              { n: 1, ip: match.cstv1_ip, pw: match.cstv1_password, viewers: match.cstv1_viewers || 0 },
              { n: 2, ip: match.cstv2_ip, pw: match.cstv2_password, viewers: match.cstv2_viewers || 0 },
            ].filter(r => r.ip);
            if (relays.length === 0) return null;
            const purple = '#a78bfa';
            return (
              <div style={{
                background: 'linear-gradient(180deg, rgba(167,139,250,0.06), rgba(167,139,250,0.02))',
                border: '1px solid rgba(167,139,250,0.22)', borderRadius: 'var(--radius-md, 10px)',
                padding: isDesktop ? '0.9rem 1rem' : '0.7rem 0.6rem', display: 'flex', flexDirection: 'column', gap: isDesktop ? '0.7rem' : '0.55rem',
                boxShadow: '0 2px 16px rgba(167,139,250,0.05)',
              }}>
                <span style={{ alignSelf: 'center', display: 'inline-flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.8rem', fontWeight: 700, color: purple, fontFamily: 'var(--font-sans)' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <path d="M23 7l-7 5 7 5V7z" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                  </svg>
                  Watch on CSTV
                </span>
                {relays.map(r => {
                  const connectStr = `connect ${r.ip}${r.pw ? `;password ${r.pw}` : ''}`;
                  const copied = cstvCopied === r.n;
                  // Fully mask the connect command by default — the entire string renders as
                  // asterisks so neither IP nor password is legible. Revealable per-relay via
                  // the eye toggle. Copy always copies the full unmasked connectStr.
                  const revealed = !!cstvRevealed[r.n];
                  // Masked view uses a FIXED-width run of dots (not one-per-char) so the row
                  // stays compact regardless of the real connect string's length. Revealing
                  // shows the true string; Copy always copies the real connectStr.
                  const displayStr = revealed ? connectStr : '••••••••••••';
                  return (
                    <div key={r.n} style={{
                      display: 'flex', alignItems: 'center', gap: isDesktop ? '0.6rem' : '0.4rem', minWidth: 0,
                      background: 'rgba(0,0,0,0.22)', border: '1px solid rgba(255,255,255,0.06)',
                      borderRadius: 'var(--radius-sm, 8px)', padding: isDesktop ? '6px 6px 6px 10px' : '5px 5px 5px 7px',
                      // Full-width row: badge + count at the left, IP centered, Copy at the right.
                      alignSelf: 'stretch',
                    }}>
                      <span style={{ fontSize: '0.66rem', fontWeight: 800, color: purple, fontFamily: 'var(--font-sans)', flexShrink: 0, letterSpacing: '0.03em', padding: '2px 7px', borderRadius: 6, background: 'rgba(167,139,250,0.12)' }}>CSTV {r.n}</span>
                      {/* Live spectator count for this relay (number only - no names on the public page). */}
                      <span title="Watching now" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.66rem', fontWeight: 700, color: r.viewers > 0 ? '#f87171' : 'var(--text-muted, #4a4a5a)', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: r.viewers > 0 ? '#f87171' : 'var(--text-muted, #4a4a5a)', display: 'inline-block' }} />
                        {r.viewers}
                      </span>
                      <span style={{ flex: 1, minWidth: 0, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: isDesktop ? '0.74rem' : '0.68rem', color: 'var(--text-secondary, #8b8b9e)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayStr}</span>
                      <button
                        onClick={() => setCstvRevealed(prev => ({ ...prev, [r.n]: !prev[r.n] }))}
                        style={{ background: 'none', border: 'none', color: revealed ? purple : 'var(--text-muted, #4a4a5a)', cursor: 'pointer', padding: '2px', display: 'inline-flex', alignItems: 'center', flexShrink: 0, transition: 'color 0.2s ease' }}
                        title={revealed ? 'Hide IP' : 'Reveal IP'}
                      >
                        {revealed ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                        )}
                      </button>
                      <button
                        onClick={() => { navigator.clipboard.writeText(connectStr); setCstvCopied(r.n); setTimeout(() => setCstvCopied(c => c === r.n ? null : c), 1800); }}
                        onMouseEnter={e => { if (!copied) { e.currentTarget.style.background = 'rgba(167,139,250,0.16)'; e.currentTarget.style.borderColor = 'rgba(167,139,250,0.4)'; e.currentTarget.style.color = purple; } }}
                        onMouseLeave={e => { if (cstvCopied !== r.n) { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.borderColor = 'var(--border-subtle, rgba(255,255,255,0.12))'; e.currentTarget.style.color = 'var(--text-secondary, #8b8b9e)'; } }}
                        style={{
                          background: copied ? 'rgba(34,197,94,0.14)' : 'rgba(255,255,255,0.02)',
                          border: `1px solid ${copied ? 'rgba(34,197,94,0.4)' : 'var(--border-subtle, rgba(255,255,255,0.12))'}`,
                          borderRadius: 'var(--radius-sm, 6px)',
                          color: copied ? 'var(--accent-green, #22c55e)' : 'var(--text-secondary, #8b8b9e)',
                          cursor: 'pointer', fontSize: '0.68rem', fontWeight: 700, fontFamily: 'var(--font-sans)',
                          display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', flexShrink: 0,
                          transition: 'background 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                        }}
                        title="Copy connect command"
                      >
                        {copied ? (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                        ) : (
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                        )}
                        {copied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* ── Round History ─────────────────────────── */}
          {false && filteredRounds.length > 0 && (
            <div className="pub-rounds-card">
              <div className="pub-rounds-label">Round History</div>
              {(() => {
                const regulation = filteredRounds.filter(r => r.round_number <= 24);
                const overtime = filteredRounds.filter(r => r.round_number > 24);
                const otRows = [];
                for (let i = 0; i < overtime.length; i += 6) {
                  otRows.push(overtime.slice(i, i + 6));
                }

                const RoundBadge = ({ r }) => (
                  <div title={`R${r.round_number}: ${r.winner_side} Win`}
                    className={`pub-round-badge ${r.winner_side === 'CT' ? 'pub-round-badge--ct' : 'pub-round-badge--t'}`}>
                    {r.round_number}
                  </div>
                );

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    {regulation.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                        {regulation.map((r, i) => (
                          <React.Fragment key={i}>
                            <RoundBadge r={r} />
                            {r.round_number === 12 && i < regulation.length - 1 && (
                              <div style={{ color: 'var(--text-muted)', margin: '0 2px', fontSize: '0.7rem' }}>|</div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                    {otRows.map((otRounds, otIdx) => (
                      <div key={`ot${otIdx}`} style={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                        <span style={{ fontSize: '0.55rem', fontWeight: 700, color: 'var(--text-muted)', marginRight: 3, minWidth: 22 }}>OT{otIdx + 1}</span>
                        {otRounds.map((r, i) => (
                          <React.Fragment key={i}>
                            <RoundBadge r={r} />
                            {i === 2 && otRounds.length > 3 && (
                              <div style={{ color: 'var(--text-muted)', margin: '0 2px', fontSize: '0.7rem' }}>|</div>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Stats Section ────────────────────────── */}
          <div className={`pub-match-content-grid${match.show_killfeed ? '' : ' pub-match-content-grid--single'}`}>
          <div className="pub-stats-card">
            {/* Map Tabs header */}
            <div className="pub-stats-header">
              <span className="pub-stats-title">{headerMapName} Statistics</span>
              {(() => {
                let mapList = [];
                try { mapList = match.map_list ? (typeof match.map_list === 'string' ? JSON.parse(match.map_list) : match.map_list) : []; } catch (_) { }
                if (mapList.length === 0) mapList = [match.map || 'Unknown'];
                if (mapList.length <= 1) return null;

                return (
                  <div style={{ display: 'flex', gap: 4 }}>
                    {mapList.map((m, i) => {
                      const mn = (m || '').replace(/^(de_|cs_)/, '') || `M${i + 1}`;
                      const isSelected = activeMapIndex === i;
                      return (
                        <button
                          key={i}
                          onClick={() => { setSelectedMapIndex(i); setSelectedOtPeriod(null); }}
                          className={`pub-map-tab ${isSelected ? 'pub-map-tab--active' : ''}`}
                        >
                          {mn}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            <div className="pub-stats-stack">
              {(() => {
                // Flux 3.0 + Swing columns only when the API sent them (not under a broadcast delay)
                const showFlux3 = [...currentStats.team1.visible, ...currentStats.team2.visible].some(p => p.flux3_rating != null);
                const headers = ['PLAYER', 'K', 'D', 'A', 'K/D', 'DMG', 'ADR', 'KAST', 'Rating', ...(showFlux3 ? ['FLUX3', 'SWING'] : []), 'HS%', 'HS', 'FK', 'TK', '2K', '3K', '4K', '5K'];
                const headerTitles = {
                  Rating: t('matchDetail.ratingHltvTitle', 'HLTV Rating 2.0 (estimate)'),
                  FLUX3: t('matchDetail.ratingFlux3Title', 'Flux 3.0 - HLTV 3.0-style rating with Round Swing'),
                  SWING: t('matchDetail.swingTitle', "Round Swing: how much the player changes their team's chance to win the round, on average per round"),
                };
                const teamEntries = [
                  { players: currentStats.team1.visible, name: match.team1_name, side: team1Side, teamId: match.team1_id, teamExists: match.team1_exists, hasAvatar: !!match.team1_avatar, score: displayScore1 },
                  { players: currentStats.team2.visible, name: match.team2_name, side: team2Side, teamId: match.team2_id, teamExists: match.team2_exists, hasAvatar: !!match.team2_avatar, score: displayScore2 },
                ];
                const ctTeam = teamEntries.find(t => t.side === 'CT') || teamEntries[0];
                const tTeam = teamEntries.find(t => t.side === 'T') || teamEntries[1];

                const renderTeamTable = ({ players, name, side, teamId, teamExists, hasAvatar, score }, showMapName = false) => (
                  <div key={`${name}-${side}`} className="pub-stats-team pub-stats-team--stacked">
                    <div
                      className="pub-stats-team-header pub-stats-team-header--scoreboard"
                      style={{
                        color: side === 'CT' ? '#22d3ee' : '#fb923c',
                        background: side === 'CT' ? 'rgba(6,182,212,0.04)' : 'rgba(251,146,60,0.04)',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {/* Small team logo before the name, when the team has an avatar */}
                        {hasAvatar && teamExists && (
                          <img
                            src={`/api/teams/${teamId}/avatar?v=${teamId}`}
                            alt=""
                            style={{ width: 20, height: 20, borderRadius: 4, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {teamExists ? (
                            <Link to={`/teams/${teamId}`} style={{ color: 'inherit', textDecoration: 'none', transition: 'opacity 0.12s' }} onMouseEnter={e => e.currentTarget.style.opacity = '0.7'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                              {name}
                            </Link>
                          ) : name}
                        </span>
                        <span
                          title={activeMapIndex === 'all' ? `Map wins (Best of ${match.best_of})` : `Map ${activeMapIndex + 1} round score`}
                          style={{
                            fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.85rem',
                            color: 'var(----text-primary)',
                            flexShrink: 0,
                          }}
                        >
                          {score}
                        </span>
                      </span>
                      <span
                        title={showMapName ? headerMapName : ''}
                        style={{
                          justifySelf: 'center',
                          maxWidth: 'min(36vw, 420px)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: showMapName ? 'var(----text-primary)' : 'transparent',
                          fontFamily: 'var(--font-display)',
                          fontSize: '0.85rem',
                          fontWeight: 800,
                          lineHeight: 1,
                          textTransform: 'capitalize', /* map names arrive lowercase (mirage) */
                          textShadow: showMapName ? '0 0 18px rgba(59,130,246,0.22)' : 'none',
                          pointerEvents: showMapName ? 'auto' : 'none',
                        }}
                      >
                        {showMapName ? headerMapName : 'Map'}
                      </span>
                      <span className="side-tag" style={{ justifySelf: 'end', background: side === 'CT' ? 'rgba(6,182,212,0.12)' : 'rgba(251,146,60,0.12)', flexShrink: 0 }}>{side}</span>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="pub-table pub-table--scoreboard">
                        <thead>
                          <tr>
                            {headers.map(h => {
                              const isRating = h === 'Rating' || h === 'FLUX3';
                              return (
                                <th
                                  key={h}
                                  title={headerTitles[h]}
                                  style={{ textAlign: h === 'PLAYER' ? 'left' : 'center', color: isRating ? 'var(----accent-cyan)' : undefined }}
                                >
                                  {h === 'Rating' ? 'HLTV 2.0' : h === 'FLUX3' ? 'FLUX 3.0' : h}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {(players || []).length > 0 ? (players || []).map((p, i) => (
                            <PlayerRow key={`${p.steam_id || p.name || i}-${i}`} p={p} />
                          )) : (
                            <tr><td colSpan={headers.length} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>No players connected</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );

                const renderRoundHistoryBar = () => {
                  if (filteredRounds.length === 0 || activeMapIndex === 'all') return null;

                  const ss1 = startingSide1 === 'T' ? 'TERRORIST' : startingSide1;
                  const mpMaxRounds = Number(match?.mp_maxrounds) || 24;
                  const mpOtMaxRounds = Number(match?.mp_overtime_maxrounds) || 6;
                  const roundsPerHalf = mpMaxRounds / 2;
                  const roundsPerOTHalf = mpOtMaxRounds / 2;
                  const topIsTeam1 = team1Side === 'CT';

                  const roundMap = {};
                  filteredRounds.forEach(r => {
                    const rn = Number(r.round_number);
                    if (!rn) return;
                    const winnerSide = r.winner_side === 'T' ? 'TERRORIST' : r.winner_side;
                    roundMap[rn] = { winnerSide, reason: r.win_reason || '' };
                  });

                  const getReasonIcon = (reason, side) => {
                    const normalized = String(reason || '').toLowerCase();
                    const iconMap = {
                      bomb_exploded: '/killfeed/explosion.svg',
                      bomb_defused: '/killfeed/defuse.svg',
                      ct_elimination: '/killfeed/skull_ct.svg',
                      t_elimination: '/killfeed/skull_t.svg',
                      time: '/killfeed/stopwatch.svg',
                    };
                    let src = iconMap[normalized];
                    if (!src && normalized.includes('defus')) src = '/killfeed/defuse.svg';
                    if (!src && normalized.includes('explod')) src = '/killfeed/explosion.svg';
                    if (!src && normalized.includes('time')) src = '/killfeed/stopwatch.svg';
                    if (!src && normalized.includes('elimination')) src = side === 'CT' ? '/killfeed/skull_ct.svg' : '/killfeed/skull_t.svg';
                    if (src) return <img src={src} alt={formatRoundEndReason(reason)} className="pub-round-flow-icon" />;

                    return (
                      <span
                        className="pub-round-flow-dot"
                        style={{ background: side === 'CT' ? '#22d3ee' : '#fb923c' }}
                      />
                    );
                  };

                  const getTeamWinnerForRound = (rn) => {
                    const data = roundMap[rn];
                    if (!data) return null;

                    let team1SideForRound;
                    if (rn <= roundsPerHalf) {
                      team1SideForRound = ss1;
                    } else if (rn <= mpMaxRounds) {
                      team1SideForRound = ss1 === 'CT' ? 'TERRORIST' : 'CT';
                    } else {
                      const otRound = rn - mpMaxRounds;
                      const otHalf = Math.ceil(otRound / roundsPerOTHalf);
                      team1SideForRound = (otHalf % 2 === 1)
                        ? (ss1 === 'CT' ? 'TERRORIST' : 'CT')
                        : ss1;
                    }

                    return {
                      team1Won: data.winnerSide === team1SideForRound,
                      side: data.winnerSide === 'TERRORIST' ? 'T' : data.winnerSide,
                      reason: data.reason,
                    };
                  };

                  const renderCell = (rn, isTop) => {
                    const result = getTeamWinnerForRound(rn);
                    const isWin = result && (isTop
                      ? (topIsTeam1 ? result.team1Won : !result.team1Won)
                      : (topIsTeam1 ? !result.team1Won : result.team1Won));
                    return (
                      <span key={`${isTop ? 'top' : 'bot'}-${rn}`} className="pub-round-flow-cell" style={{ minWidth: 0 }} title={result ? `R${rn}: ${result.side} - ${formatRoundEndReason(result.reason)}` : `R${rn}`}>
                        {isWin ? getReasonIcon(result.reason, result.side) : null}
                      </span>
                    );
                  };

                  // Each row centres on the half-divider: half 1 hugs the divider from the
                  // LEFT (right-aligned), half 2 from the RIGHT (left-aligned). Both halves
                  // are flex:1 so the divider stays pinned to the true centre.
                  const renderRow = (slots, dividerAfter, isTop) => {
                    const leftSlots = slots.filter(rn => rn <= dividerAfter);
                    const rightSlots = slots.filter(rn => rn > dividerAfter);
                    return (
                      <div className={`pub-round-flow-row pub-round-flow-row--${isTop ? 'ct' : 't'}`} style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ flex: '1 1 0', display: 'flex', justifyContent: 'flex-end', overflow: 'hidden' }}>
                          {leftSlots.map(rn => renderCell(rn, isTop))}
                        </div>
                        <span className="pub-round-flow-divider" style={{ flexShrink: 0 }} />
                        <div style={{ flex: '1 1 0', display: 'flex', justifyContent: 'flex-start', overflow: 'hidden' }}>
                          {rightSlots.map(rn => renderCell(rn, isTop))}
                        </div>
                      </div>
                    );
                  };

                  const renderRows = (slots, dividerAfter, label = null) => (
                    <div className="pub-round-flow-set" key={label || 'reg'}>
                      {label && <div className="pub-round-flow-label">{label}</div>}
                      {renderRow(slots, dividerAfter, true)}
                      {renderRow(slots, dividerAfter, false)}
                    </div>
                  );

                  // How many OT periods actually have rounds played.
                  const highestRound = Math.max(...filteredRounds.map(r => Number(r.round_number) || 0), 0);
                  const otPeriodCount = highestRound > mpMaxRounds
                    ? Math.ceil((highestRound - mpMaxRounds) / mpOtMaxRounds)
                    : 0;

                  // Clamp any stale OT selection (e.g. after switching to a map with fewer OTs).
                  const activeOt = (selectedOtPeriod && selectedOtPeriod <= otPeriodCount) ? selectedOtPeriod : null;

                  // Toggle model (matches the admin page): show EITHER regulation OR one OT
                  // period in a fixed-width bar — never append a growing list of OT rows.
                  let slots, dividerAfter;
                  if (!activeOt) {
                    slots = Array.from({ length: mpMaxRounds }, (_, i) => i + 1);
                    dividerAfter = roundsPerHalf;
                  } else {
                    const otStart = mpMaxRounds + (activeOt - 1) * mpOtMaxRounds + 1;
                    slots = Array.from({ length: mpOtMaxRounds }, (_, i) => otStart + i);
                    dividerAfter = otStart + roundsPerOTHalf - 1;
                  }

                  return (
                    <div className="pub-round-flow">
                      {otPeriodCount > 0 && (
                        <div className="pub-round-flow-toggle">
                          <button
                            className={`pub-round-flow-toggle-btn ${!activeOt ? 'pub-round-flow-toggle-btn--active' : ''}`}
                            onClick={() => setSelectedOtPeriod(null)}
                          >REG</button>
                          {Array.from({ length: otPeriodCount }, (_, i) => (
                            <button
                              key={i + 1}
                              className={`pub-round-flow-toggle-btn ${activeOt === i + 1 ? 'pub-round-flow-toggle-btn--active' : ''}`}
                              onClick={() => setSelectedOtPeriod(i + 1)}
                            >OT{i + 1}</button>
                          ))}
                        </div>
                      )}
                      {renderRows(slots, dividerAfter)}
                    </div>
                  );
                };

                return (
                  <>
                    {renderTeamTable(ctTeam, true)}
                    {renderRoundHistoryBar()}
                    {renderTeamTable(tTeam)}
                  </>
                );
              })()}
            </div>
          </div>

          {!!match.show_killfeed && (
            <aside
              className="pub-killfeed-card"
            >
              <div className="pub-killfeed-header">
                <span className="pub-stats-title">{t('match.killFeed', 'Kill Feed')}</span>
              </div>
              <div className="pub-killfeed-body">
                {/* newest round on top: nothing to auto-scroll */}
                <MatchFeed matchId={match.id} api={publicApi} mapIndex={activeMapIndex} live={match.status === 'live'} refreshKey={feedTick} />
              </div>
            </aside>
          )}
          </div>
        </div>
        <Footer />

        {/* Desktop map-veto overlay - renders on top of the match page (which stays
            behind it) with a fade + scale-in animation. Mobile uses full-page nav. */}
        <AnimatePresence>
          {vetoOverlayRoom && (
            <motion.div
              key="veto-overlay-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setVetoOverlayRoom(null)}
              style={{
                position: 'fixed', inset: 0, zIndex: 2000,
                background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                padding: '3vh 1rem', overflowY: 'auto',
              }}
            >
              <motion.div
                key="veto-overlay-panel"
                initial={{ opacity: 0, y: 24, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 24, scale: 0.97 }}
                transition={{ type: 'spring', stiffness: 320, damping: 30 }}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', maxWidth: 1000, position: 'relative' }}
              >
                <button
                  onClick={() => setVetoOverlayRoom(null)}
                  aria-label="Close map veto"
                  style={{
                    position: 'absolute', top: -6, right: -6, zIndex: 2, width: 34, height: 34,
                    borderRadius: '50%', border: '1px solid var(--border-subtle, rgba(255,255,255,0.12))',
                    background: 'var(--bg-card, #111118)', color: 'var(--text-secondary, #8b8b9e)',
                    cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary, #e8e8ed)'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary, #8b8b9e)'; }}
                >✕</button>
                <Veto roomId={vetoOverlayRoom} embedded onClose={() => setVetoOverlayRoom(null)} />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // Only reached with a match id that did not load: the list view is public/PublicMatchList.js
  return null;
}

export default PublicMatches;
